import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AlertController, LoadingController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';

import { isEmpty, isNull } from 'lodash-es';
import { from, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, finalize, map } from 'rxjs/operators';
import { v4 } from 'uuid';

import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { LoadingService } from '../../misc/services/loading.service';
import { PushNotificationsService } from '../../misc/services/push-notifications.service';
import { EndUsersGetResponse } from '../../typings/end-users';
import {
  MobileUsersGetResponse,
  MobileUsersPatchResponse,
  MobileUsersUpsertResponse
} from '../../typings/mobile-users';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromConfigSelectors from '../selectors/config.selectors';
import * as fromDataProviderSelectors from '../selectors/data-provider.selectors';
import * as fromDeviceSelectors from '../selectors/device.selectors';
import * as fromOrganizationSelectors from '../selectors/organization.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private loadingController = inject(LoadingController);
  private loadingService = inject(LoadingService);
  private pushNotificationsService = inject(PushNotificationsService);
  private router = inject(Router);
  private store = inject(Store);

  upsertMobileUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.registerMobileUser),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
        this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsAutoSubscribe)),
        this.store.pipe(select(fromDeviceSelectors.selectId)),
        this.store.pipe(select(fromConfigSelectors.selectTermVersion)),
        this.store.pipe(select(fromDataProviderSelectors.selectUsername))
      ),
      switchMap(
        ([
          {
            payload: { data, isOnboardingFlow }
          },
          parseType,
          appName,
          securityCode,
          autoSubscribeFarmAlerts,
          deviceId,
          tosVersion,
          providerUsername
        ]) => {
          const body: {
            appName?: string | null;
            autoSubscribeFarmAlerts?: boolean | null;
            company?: string | null;
            email?: string | null;
            firstName?: string | null;
            lastName?: string | null;
            newDeviceId?: string;
            newFirebaseToken?: string;
            phone?: string | null;
            providerUsername?: string | null;
            securityCode?: string | null;
            tosVersion?: number | null;
          } = {
            appName,
            autoSubscribeFarmAlerts,
            providerUsername,
            securityCode,
            tosVersion,
            ...data
          };

          if (deviceId) {
            body.newDeviceId = deviceId;
          }
          return this.adminPortalApiService
            .sendRequest('POST', `/v2/mobile-users`, {
              body,
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map((data: MobileUsersUpsertResponse) =>
                fromUserActions.registerMobileUserSuccess({ payload: { id: data.id, isOnboardingFlow } })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromUserActions.registerMobileUserFailure({
                    payload: { error }
                  })
                )
              )
            );
        }
      )
    )
  );

  registerMobileUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromUserActions.registerMobileUserSuccess),
        concatMap(() => this.pushNotificationsService.init())
      ),
    {
      dispatch: false
    }
  );

  upsertMobileUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.registerMobileUserSuccess),
      withLatestFrom(
        this.store.pipe(select(fromDataProviderSelectors.selectIsBlackKnight)),
        this.store.pipe(select(fromSettingSelectors.selectParseType))
      ),
      switchMap(
        ([
          {
            payload: { id, isOnboardingFlow }
          },
          dataProviderRequiresAuthentication,
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/mobile-users/${id}`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType
              })
            })
            .pipe(
              switchMap((data: MobileUsersGetResponse) => {
                const actions: Action[] = [
                  fromUserActions.loadMobileUserSuccess({
                    payload: { data, id }
                  })
                ];
                if (isOnboardingFlow) {
                  let path: string[];
                  if (dataProviderRequiresAuthentication) {
                    path = ['onboarding', 'login'];
                  } else {
                    path = ['/onboarding', 'change-icon'];
                  }
                  this.router.navigate(path);
                }
                return actions;
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromUserActions.loadMobileUserFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadEndUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.loadEndUserDetails),
      switchMap(({ payload: { referralCode } }) => {
        return this.adminPortalApiService.sendRequest('GET', `/v2/end-users/details/${referralCode}`).pipe(
          map((payload: EndUsersGetResponse) =>
            fromUserActions.loadEndUserDetailsSuccess({
              payload
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              fromUserActions.loadEndUserDetailsFailure({
                payload: { error }
              })
            )
          )
        );
      })
    )
  );

  updateMobileUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.updateMobileUser),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromUserSelectors.selectId))
      ),
      filter((data) => !isNull(data[2])),
      switchMap(([{ payload }, parseType, userId]) =>
        this.adminPortalApiService
          .sendRequest('PATCH', `/v2/mobile-users/${userId}`, {
            body: payload,
            params: this.adminPortalApiService.getQueryParams({
              parseType: parseType
            })
          })
          .pipe(
            switchMap((data: MobileUsersPatchResponse) => [
              fromUserActions.updateMobileUserSuccess({ payload: { data, id: data.id } }),
              fromUserActions.reloadMobileUser()
            ]),
            catchError((error: HttpErrorResponse) =>
              of(
                fromUserActions.updateMobileUserFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  reloadMobileUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.reloadMobileUser),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectParseType))
      ),
      map((data) => ({
        id: data[1] ? data[1] : '',
        parseType: data[2]
      })),
      filter(({ id }) => !isEmpty(id)),
      switchMap(({ id, parseType }) =>
        this.adminPortalApiService
          .sendRequest('GET', `/v2/mobile-users/${id}`, {
            params: this.adminPortalApiService.getQueryParams({
              parseType
            })
          })
          .pipe(
            map((data: MobileUsersGetResponse) =>
              fromUserActions.loadMobileUserSuccess({
                payload: {
                  data,
                  id
                }
              })
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromUserActions.loadMobileUserFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  deleteData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.deleteData),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromUserSelectors.selectId))
      ),
      map((data) => ({ parseType: data[1], userId: data[2] })),
      switchMap(({ parseType, userId }) =>
        this.adminPortalApiService
          .sendRequest('DELETE', `/v2/mobile-users/${userId}`, {
            params: this.adminPortalApiService.getQueryParams({
              parseType: parseType
            })
          })
          .pipe(
            concatMap(async () => {
              const alert = await this.alertController.create({
                buttons: [
                  {
                    role: 'cancel',
                    text: 'Ok'
                  }
                ],
                header: 'Success',
                message:
                  'Your data is now deleted.  Be sure to log out from any other installations of the app you may be using.'
              });
              await alert.present();
              await alert.onDidDismiss();
            }),
            switchMap(() => {
              const id = v4();
              this.loadingService.setLoading(id, true);
              setTimeout(async () => {
                await this.router.navigate(['']);
                this.loadingService.setLoading(id, false);
              }, 1000);
              return [fromPersistedStateActions.persistedStateReset()];
            }),
            catchError((error: HttpErrorResponse) => {
              this.alertController
                .create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message:
                    'A problem occurred while deleting your personal data. If this problem persists please contact support@redshedtechnology.'
                })
                .then(async (alert) => {
                  await alert.present();
                });
              return of(
                fromUserActions.deleteDataFailure({
                  payload: { error }
                })
              );
            })
          )
      )
    )
  );

  loadPersistedStateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.loadPersistedStateSuccess),
      map(() => fromUserActions.reloadMobileUser())
    )
  );

  migratePersistedOldStateReferralCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      concatMap(([{ payload }, parseType]) =>
        from(
          this.loadingController.create({
            backdropDismiss: false,
            message: 'Migrating user data...',
            showBackdrop: true,
            translucent: true
          })
        ).pipe(
          concatMap(async (loader) => {
            await loader.present();
            return loader;
          }),
          switchMap((loader) =>
            this.adminPortalApiService
              .sendRequest('GET', `/v2/mobile-users/${payload.state.user.id}`, {
                params: this.adminPortalApiService.getQueryParams({
                  parseType
                })
              })
              .pipe(
                map((data: MobileUsersGetResponse) =>
                  fromPersistedStateActions.migratePersistedOldStateUserDataSuccess({
                    payload: {
                      data,
                      id: payload.state.user.id,
                      state: payload.state
                    }
                  })
                ),
                catchError(() =>
                  of(
                    fromPersistedStateActions.migratePersistedOldStateFailure({
                      payload: {
                        error: new Error('Unable to validate and migrate user data.')
                      }
                    })
                  )
                ),
                finalize(async () => await loader.dismiss())
              )
          )
        )
      )
    )
  );
}
